const windowRect = () => {
  const vw = window.innerWidth || document.documentElement.clientWidth;
  const vh = window.innerHeight || document.documentElement.clientHeight;
  return { left: 0, top: 0, right: vw, bottom: vh };
};

// Axis-aligned rectangle intersection helper
const intersectRects = (a, b) => {
  const left = Math.max(a.left, b.left);
  const top = Math.max(a.top, b.top);
  const right = Math.min(a.right, b.right);
  const bottom = Math.min(a.bottom, b.bottom);
  return { left, top, right, bottom };
};

// Determine what portion of the page the user can see for a given element:
// start at window or a custom root, then intersect with any clipping ancestors.
const effectiveViewportFor = (el, root) => {
  let rect = root && root.isConnected ? root.getBoundingClientRect() : windowRect();
  let node = el.parentElement;
  while (node && node !== document.documentElement && node !== document.body) {
    if (node === root) break;
    const cs = getComputedStyle(node);
    const clipsX = cs.overflowX && cs.overflowX !== 'visible';
    const clipsY = cs.overflowY && cs.overflowY !== 'visible';
    if (clipsX || clipsY) {
      const nr = node.getBoundingClientRect();
      rect = intersectRects(rect, nr);
      if (rect.right <= rect.left || rect.bottom <= rect.top) break;
    }
    node = node.parentElement;
  }
  return rect;
};

/**
  PriorityQueue for running DOM-related jobs with visibility and priority:
  - Runs Promise-returning jobs tied to DOM elements
  - Prioritizes what the user can actually see (supports nested clipping/scroll containers)
  - Only queued jobs reorder; running/done remain in place
  - Concurrency is adjustable; triggers are coalesced via rAF for perf
 */
class PriorityQueue {
  constructor(opts = {}) {
    this.concurrency = Math.max(1, opts.concurrency || 5);
    this._concurrency = this.concurrency;
    this.root = opts.root || null;
    // Observe visibility changes; we still coalesce all work with rAF
    this._io = 'IntersectionObserver' in window
      ? new IntersectionObserver(() => {
        this._scheduleResort();
      }, {
        root: this.root,
        rootMargin: opts.rootMargin || '0px',
        threshold: opts.threshold ?? [0],
      })
      : null;
    this.jobs = new Map();
    this.queued = [];
    this.running = new Set();
    this._id = 0;
    this._resortScheduled = false;
    this._destroyed = false;

    this._onScroll = () => {
      if (this._destroyed) return;
      this._scheduleResort();
    };
    window.addEventListener('scroll', this._onScroll, { passive: true });
    window.addEventListener('resize', this._onScroll, { passive: true });
  }

  add(el, taskFn, priority = null) {
    if (!el || typeof taskFn !== 'function') throw new Error('add(el, taskFn): invalid arguments');
    const id = `job_${++this._id}`;
    const job = { id, el, taskFn, state: 'queued', priority };
    this.jobs.set(id, job);
    this.queued.push(id);
    if (this._io) this._io.observe(el);
    this._scheduleResort();
    return id;
  }

  sort() {
    this._scheduleResort();
  }

  pause() {
    this._paused = true;
  }

  resume() {
    this._paused = false;
    this._drain();
  }

  destroy() {
    if (this._destroyed) return;
    this._destroyed = true;
    window.removeEventListener('scroll', this._onScroll);
    window.removeEventListener('resize', this._onScroll);
    if (this._io) this._io.disconnect();
  }

  // Coalesce multiple triggers (scroll/resize/IO/add) into one resort+drain per frame
  _scheduleResort() {
    if (this._resortScheduled) return;
    this._resortScheduled = true;
    requestAnimationFrame(() => {
      this._resortScheduled = false;
      if (this._destroyed) return;
      this._resortQueued();
      this._drain();
    });
  }

  // Reorder queued jobs: visible first (in-viewport), then hidden; each group top→bottom
  _resortQueued() {
    if (this.queued.length) {
      this.queued = this.queued.filter((id) => {
        const job = this.jobs.get(id);
        if (!job) return false;
        if (!job.el.isConnected) { this._untrackJob(job); return false; }
        return true;
      });
    } else {
      // If no jobs are queued, nothing to resort
      return;
    }

    // Partition into visible and hidden relative to the element's effective viewport
    const visible = [];
    const hidden = [];

    // Single pass to compute visibility and a vertical sort key relative to the viewport
    for (const id of this.queued) {
      const j = this.jobs.get(id);
      const r = j.el.getBoundingClientRect();
      const vp = effectiveViewportFor(j.el, this.root);
      const isVisible = r.right > vp.left && r.left < vp.right && r.bottom > vp.top && r.top < vp.bottom;
      const top = r.top - vp.top;
      (isVisible ? visible : hidden).push({ id, top, priority: j.priority });
    }

    // Stable-ish ordering within groups by vertical position, then concatenate
    visible.sort((a, b) => a.top - b.top);
    hidden.sort((a, b) => {
      if (a.priority !== null && b.priority !== null) {
        // Sort lower priority value first
        return a.priority - b.priority;
      }
      if (a.priority !== null) return -1;
      if (b.priority !== null) return 1;
      return a.top - b.top;
    });

    if (visible.length) {
      // If there are visible items, temporarily set concurrency to that count
      // to prevent hidden jobs from competing with visible ones
      this.concurrency = Math.min(visible.length, this._concurrency);
    }
    this.queued = [...visible.map((x) => x.id), ...hidden.map((x) => x.id)];
  }

  _drain() {
    if (this._destroyed) return;
    // Pull from the front until we hit the concurrency limit
    while (this.running.size < this.concurrency && this.queued.length > 0) {
      if (this._paused) return;
      const id = this.queued.shift();
      const job = this.jobs.get(id);
      if (!job || job.state !== 'queued') {
        continue;
      }
      if (!job.el.isConnected) {
        this._untrackJob(job);
        continue;
      }
      this._start(job);
    }
    // Restore concurrency to the original value if we had adjusted it
    this.concurrency = this._concurrency;
  }

  async _start(job) {
    job.state = 'running';
    this.running.add(job.id);
    // Stop observing while running to avoid unnecessary IO traffic
    if (this._io && job.el) this._io.unobserve(job.el);
    try {
      // Ensure a Promise; completion (resolve/reject) frees a slot
      await job.taskFn(job.el, job);
    } finally {
      this._finish(job);
    }
  }

  _finish(job) {
    job.state = 'done';
    this.running.delete(job.id);
    this._drain();
  }

  _untrackJob(job) {
    // Remove from all internal structures and stop observing the element
    if (this._io && job.el) this._io.unobserve(job.el);
    this.jobs.delete(job.id);
    const i = this.queued.indexOf(job.id);
    if (i >= 0) this.queued.splice(i, 1);
    this.running.delete(job.id);
  }
}

export default PriorityQueue;
